import {
  Component,
  h,
  Host,
  Element,
  State,
  ComponentInterface,
  Watch,
  Listen,
  Event,
  EventEmitter,
} from '@stencil/core';
import type { AloliaSearchResultDetail, SearchIndex } from '../../interface';

const filterOldPosts =
  'created > ' + Math.floor((Date.now() - 63115200000) / 1000);

/**
 * Nanopore Global Search results component
 * to be used in conjunction with [Globla-Nav](/story/nano-compounds-global-nav).
 * A shortcut / helper which combines algolia components.
 * There should only be one <nano-global-search-results> per page
 * and should be a direct child of <nano-global-nav>
 */
@Component({
  tag: 'nano-global-search-results',
  styleUrl: 'global-search-results.scss',
  shadow: true,
})
export class GlobalSearchResults implements ComponentInterface {
  private algoliaResultsEle: HTMLNanoAlgoliaResultsElement;
  private filtersDiv: HTMLElement;
  private openFilterBtn: HTMLButtonElement;
  private ro: ResizeObserver;

  @State() algoliaEle: HTMLNanoAlgoliaElement;
  @State() currentWidth: number;
  @State() apiKey: string;
  @State() appId: string;
  @State() index: SearchIndex;

  @Element() private el: HTMLNanoGlobalSearchResultsElement;

  /** Fired when the user clicks the 'back' button / closes the search panel
   * Calling `event.preventDefault()` will prevent it from being closed.
   */
  @Event() nanoSearchGoBack: EventEmitter;

  @Watch('algoliaEle')
  @Watch('apiKey')
  @Watch('appId')
  handleAlgoliaCredsChange() {
    if (!this.apiKey || !this.appId || !this.algoliaEle) return;
    this.algoliaEle.apiKey = this.apiKey;
    this.algoliaEle.appId = this.appId;
  }

  @Watch('algoliaEle')
  @Watch('index')
  handleAlgoliaIndexChange() {
    if (!this.index || !this.algoliaEle) return;
    this.algoliaEle.searchIndex = this.index;
  }

  @Listen('nanoSearchReset', { target: 'body' })
  handleGlobalNavReset(e: CustomEvent & { target: HTMLElement }) {
    if (e.target.tagName !== 'NANO-GLOBAL-NAV') return;
    this.algoliaResultsEle = null;
    this.goback();
  }

  @Listen('nanoSearchResult', { target: 'body' })
  handleGlobalNavSearch(e: CustomEvent & { target: HTMLElement }) {
    if (e.target.tagName !== 'NANO-GLOBAL-NAV') return;
    const {
      detail: { results, client },
    } = e as { detail: AloliaSearchResultDetail };
    const result = results.find((result) => result.selected);

    this.apiKey = client.apiKey;
    this.appId = client.appId;
    this.index = {
      name: result.indexName,
      index: result.index,
      domain: result.domain,
      filters: result.filters,
    };
    this.algoliaEle.query = result.query;
  }

  @Listen('nanoChange')
  handleReorder(e: CustomEvent & { target: HTMLNanoSelectElement }) {
    if (
      e
        .composedPath()
        .find(
          (ele: HTMLElement) =>
            ele.classList && ele.classList.contains('indexchange')
        ) &&
      !!this.algoliaResultsEle
    ) {
      this.changeOrder(e);
    }
  }

  @Listen('nanoResultsShown')
  attachListeners(e: CustomEvent & { target: HTMLElement }) {
    if (
      !e
        .composedPath()
        .find((ele: HTMLElement) => ele.id && ele.id === 'main-search')
    )
      return;

    this.algoliaResultsEle = this.algoliaEle.querySelector(
      '#main-search-results'
    );

    // add results listeners
    this.attachResultListeners();

    this.algoliaEle.querySelectorAll('.filters-reset').forEach((item) => {
      item.addEventListener('click', () => {
        this.removeAllFilters();
      });
    });

    this.algoliaEle.querySelectorAll('.close-filters').forEach((item) => {
      item.addEventListener('click', () => {
        this.closefilters();
      });
    });

    const filterWrap = this.algoliaEle.querySelector('.filters-wrap');
    if (filterWrap) {
      filterWrap.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    this.filtersDiv = this.algoliaEle.querySelector('#main-search-filters');

    const oldResults = this.algoliaEle.querySelector('.old-posts');
    if (oldResults) {
      oldResults.addEventListener(
        'change',
        (e: Event & { target: HTMLInputElement }) => {
          this.toggleOldResults(e.target.checked);
        }
      );
    }
  }

  @Listen('nanoNewResults', { target: 'body' })
  attachResultListeners(e?: CustomEvent & { target: HTMLElement }) {
    if (e && e.composedPath().find((el) => el === this.algoliaEle)) {
      this.algoliaResultsEle = this.algoliaEle.querySelector(
        'nano-algolia-results'
      );
    }
    if (!this.algoliaResultsEle) return;
    const resultEle = this.algoliaResultsEle;

    resultEle.querySelectorAll('.remove-filter').forEach((item) => {
      item.addEventListener('click', (clickEv: MouseEvent) => {
        const filter = clickEv.target as HTMLElement;
        this.removeFilter(filter.dataset.filter, filter.dataset.filterVal);
      });
    });

    resultEle.querySelectorAll('.back-btn').forEach((item) => {
      item.addEventListener('click', () => {
        this.goback();
      });
    });

    resultEle.querySelectorAll('.topbar__show-filters').forEach((item) => {
      this.openFilterBtn = item as HTMLButtonElement;
      item.addEventListener('click', () => {
        this.filtersDiv.tabIndex = -1;
        this.filtersDiv.classList.add('show');
        this.filtersDiv.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        setTimeout(() => this.filtersDiv.focus(), 20);
      });
    });

    const tags = Array.from(resultEle.querySelectorAll('.result__tags-tag'));
    tags.forEach((tag) => {
      tag.removeEventListener('click', this.tagClick);
      tag.addEventListener('click', this.tagClick);
    });
  }

  private tagClick = (e: MouseEvent & { target: HTMLElement }) => {
    e.preventDefault();
    let tagVal = e.target.dataset.value;
    tagVal = tagVal === 'publication' ? 'publications' : tagVal;
    tagVal = tagVal.toLowerCase();

    const filter: HTMLNanoAlgoliaFilterElement = this.algoliaEle.querySelector(
      'nano-algolia-filter[filter-name="tags"]'
    );
    if (filter.value.indexOf(tagVal) === -1) {
      filter.value = filter.value ? [...filter.value, tagVal] : [tagVal];
    }
  };

  private changeOrder(ev) {
    this.algoliaEle.replicaIndex = ev.detail.value;
  }

  private goback() {
    const goBackEvent = this.nanoSearchGoBack.emit();
    if (goBackEvent.defaultPrevented) return;

    this.removeAllFilters();
    this.algoliaEle.query = '';
    this.algoliaEle.showResults = false;
  }

  private removeFilter(facet, filter) {
    this.algoliaEle.removeFilters(facet, filter);
  }

  private removeAllFilters() {
    this.algoliaEle.removeFilters();
  }

  private closefilters() {
    if (!this.filtersDiv) return;
    this.filtersDiv.tabIndex = null;
    this.filtersDiv.classList.remove('show');
    this.filtersDiv.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    setTimeout(() => this.openFilterBtn.focus(), 20);
  }

  private toggleOldResults(show: boolean) {
    if (show)
      this.algoliaEle.filters = this.algoliaEle.filters.filter(
        (f) => !f.startsWith('created > ')
      );
    else this.algoliaEle.filters = [...this.algoliaEle.filters, filterOldPosts];
  }

  componentDidLoad() {
    this.ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (!entry.contentRect.width) return;
        this.currentWidth = entry.contentRect.width;
      }
    });
    this.ro.observe(this.el);
    this.algoliaEle.filters = [filterOldPosts]; // only get last 2 years
  }

  render() {
    return (
      <Host
        class={{ small: this.currentWidth < 677 }}
        dir={(this.el.ownerDocument as Document).dir === 'rtl' ? 'rtl' : false}
      >
        <nano-algolia
          store-id="searchq"
          store-method="url-hash-push"
          class="main-search__wrap sc-nano-global-search-results"
          ref={(ele) => requestAnimationFrame(() => (this.algoliaEle = ele))}
          id="main-search"
        >
          <script type="text/template" slot="template">
            {`<div class="main-search sc-nano-global-search-results">
              <nano-algolia-results id="main-search-results" class="main-search__results sc-nano-global-search-results" infinite-scroll>
                <script type="text/template" slot="start-template">
                  <div class="sc-nano-global-search-results main-search__topbar topbar">
                    <button class="sc-nano-global-search-results topbar__bkbtn back-btn icon-button">
                      <nano-icon class="sc-nano-global-search-results" name="light/chevron-left" size="small"></nano-icon>
                    </button>
                    <div>
                      <em class="sc-nano-global-search-results topbar__query">'{{ it.query }}' - </em> <strong>{{ it.totalHitsWithFilters }}</strong> Results
                    </div>

                    <div class="sc-nano-global-search-results topbar__filtering">
                      {{ @if (it.results.length) }}

                      {{ @if (it.indexName === 'All') }}
                        <nano-select class="sc-nano-global-search-results topbar__order topbar__order--www indexchange" value="{{ it.index }}" hide-label="true" label="Sort order" show-inline-error="false">
                          <nano-option value="all_prod_en">Sort by: Most relevant</nano-option>
                          <nano-option value="all_prod_en_date_desc">Sort by: Newest</nano-option>
                          <nano-option value="all_prod_en_date_asc">Sort by: Oldest</nano-option>
                          <nano-option value="all_prod_en_activity_date_desc">Sort by: Recent activity</nano-option>
                        </nano-select>
                        {{ /if }}

                        {{ @if (it.indexName === 'www') }}
                        <nano-select class="sc-nano-global-search-results topbar__order topbar__order--www indexchange" value="{{ it.index }}" hide-label="true" label="Sort order" show-inline-error="false">
                          <nano-option value="cws_english_en">Sort by: Most relevant</nano-option>
                          <nano-option value="cws_english_en_date_desc">Sort by: Newest</nano-option>
                          <nano-option value="cws_english_en_date_asc">Sort by: Oldest</nano-option>
                        </nano-select>
                        {{ /if }}

                        {{ @if (it.indexName === 'Resources') }}
                        <nano-select class="sc-nano-global-search-results topbar__order topbar__order--www indexchange" value="{{ it.index }}" hide-label="true" label="Sort order" show-inline-error="false">
                          <nano-option value="cws_english_resources_en">Sort by: Most relevant</nano-option>
                          <nano-option value="cws_english_resources_en_featured_desc">Sort by: Newest</nano-option>
                          <nano-option value="cws_english_resources_en_featured_asc">Sort by: Oldest</nano-option>
                        </nano-select>
                        {{ /if }}

                        {{ @if (it.indexName === 'Store') }}
                        <nano-select class="sc-nano-global-search-results topbar__order topbar__order--www indexchange" value="{{ it.index }}" hide-label="true" label="Sort order" show-inline-error="false">
                          <nano-option value="store_prod">Sort by: Most relevant</nano-option>
                          <nano-option value="store_prod_date_desc">Sort by: Newest</nano-option>
                          <nano-option value="store_prod_date_asc">Sort by: Oldest</nano-option>
                        </nano-select>
                        {{ /if }}

                        {{ @if (it.indexName === 'Community') }}
                        <nano-select class="sc-nano-global-search-results topbar__order topbar__order--community indexchange" value="{{ it.index }}" hide-label="true" label="Sort order" show-inline-error="false">
                          <nano-option value="community_prod">Sort by: Most relevant</nano-option>
                          <nano-option value="community_prod_date_desc">Sort by: Newest</nano-option>
                          <nano-option value="community_prod_date_asc">Sort by: Oldest</nano-option>
                          <nano-option value="community_prod_activity_date_desc">Sort by: Recent activity</nano-option>
                        </nano-select>
                        {{ /if }}
                      {{ /if }}

                      <button class="sc-nano-global-search-results topbar__show-filters filters-title" aria-controls="main-search-filters">
                        {{ @if (it.appliedFilters && it.appliedFilters.length > 0) }}
                          <span class="sc-nano-global-search-results topbar__filter-count">
                            {{ (it.appliedFilters.reduce(function(a, b) { return a + (b.values ? b.values.length : 0) }, 0)) }}
                          </span>
                        {{ /if }}
                        <nano-icon class="sc-nano-global-search-results" name="light/filter"></nano-icon>
                      </button>
                    </div>
                  </div>

                  {{ @if (it.results.length) }}
                    <div class="sc-nano-global-search-results main-search__applied-filters">
                      {{ @if (it.appliedFilters) }}
                        {{ @each(it.appliedFilters) => filterObj }}
                          {{ @each(filterObj.values) => filterVal }}
                            <span class="sc-nano-global-search-results main-search__applied-filter">
                              {{ filterVal | public_name }}
                              <button class="sc-nano-global-search-results icon-button remove-filter" data-filter="{{ filterObj.name }}" data-filter-val="{{ filterVal }}">
                                <nano-icon class="sc-nano-global-search-results" name="light/times"></nano-icon>
                              </button>
                            </span>
                          {{ /each }}
                        {{ /each }}
                      {{ #else }}
                        <span class="sc-nano-global-search-results main-search__applied-nofilter">&nbsp;</span>
                      {{ /if }}
                    </div>
                  {{ /if }}
                </script>

                <script type="text/template" slot="result-template">
                  <!-- START NO CONTENT -->
                  {{ @if ((!it.results || !it.results.length)) }}
                    <div class="search-empty">
                      <nano-icon name="light/search" class="search-empty-icon"></nano-icon>
                      <h2>No matches found...</h2>
                      <p>Please try a different search query.</p>
                    </div>
                  {{ /if }}
                  <!-- END NO CONTENT -->
                  {{ @if (it.totalHitsWithFilters > 0) }}
                    {{ @each(it.results) => result }}
                      {{ contentType = result.content_type ? result.content_type : result.type }}
                      <li class="sc-nano-global-search-results">
                        <div class="sc-nano-global-search-results result">
                          <h4 class="sc-nano-global-search-results result__title" data-icon-set="{{(iconSet = false)}}">
                            <a href="{{ result.url | abs_url(it, result) }}">
                            {{ result._snippetResult.title.value | safe }}
                            {{ @if (contentType === 'technical_document' || contentType === 'literature' || contentType === 'white_paper' || contentType === 'protocol') }}
                              <span class="sc-nano-global-search-results result__pdf">PDF</span>
                            {{ /if }}
                            </a>
                          </h4>
                          {{ @if (result.authors) }}
                            {{ @if (typeof result.authors === 'string') }}
                              <a class="sc-nano-global-search-results result__authors" href="{{ result.url | abs_url(it, result) }}">Authors: {{ result.authors | trim_to(50) }}</a>
                            {{ #else }}
                              <a class="sc-nano-global-search-results result__authors" href="{{ result.url | abs_url(it, result) }}">
                                {{ @each(result.authors) => author, index }}
                                  <!-- {{ @if(index == 0)}} -->
                                    Athors: {{ author | trim_to(60) }}
                                  <!-- {{ /if }} -->
                                {{ /each }}
                              </a>
                            {{ /if }}
                          {{ /if }}
                          {{ @if (contentType === 'video' || contentType === 'lc_lightning_talk' ) }}
                            {{ @if (result.name) }}
                              <a class="sc-nano-global-search-results result__authors" href="{{ result.url | abs_url(it, result) }}">Speaker: {{ result.name | trim_to(50) }}</a>
                            {{ /if }}
                            <a class="sc-nano-global-search-results result__video-body" href="{{ result.url | abs_url(it, result) }}">
                              {{ @if (result.image) }}
                                <div class="sc-nano-global-search-results result__image" style="background-image: url('{{ result.image | abs_url(it, result) }}')">
                                  <nano-icon class="sc-nano-global-search-results" name="duotone/play-circle"></nano-icon>
                                </div>
                              {{ /if }}
                              <div class="sc-nano-global-search-results result__body">
                                <div class="sc-nano-global-search-results result__description">
                                  {{ @if (result.description) }}
                                    {{ result._snippetResult.description.value | safe }}
                                  {{ /if }}
                                </div>
                              </div>
                              <div class="sc-nano-global-search-results result__meta">
                                {{ @if (result.type || result.content_type) }}
                                  <div class="sc-nano-global-search-results result__type">
                                  {{ @if (contentType === 'lc_lightning_talk') }}
                                    Presentation
                                  {{ #else }}
                                    {{ contentType }}
                                  {{ /if }}
                                  </div>
                                {{ /if }}
                                {{ @if (result.tags) }}
                                  <div class="sc-nano-global-search-results result__tags">
                                    {{ @if (typeof result.tags === 'string') }}
                                      <button data-value="{{ result.tags | lowercase }}" class="sc-nano-global-search-results result__tags-tag">{{ result.tags }}</button>
                                    {{ #else }}
                                      {{ @each(result.tags) => tag, index }}
                                        {{ @if(index < 5)}}
                                          {{ @if (tag !== "No video" && tag !== "Resources" && tag !== "Video") }}
                                            <button data-value="{{ tag | lowercase }}" class="sc-nano-global-search-results result__tags-tag">{{ tag }}</button>
                                          {{ /if }}
                                        {{ /if }}
                                      {{ /each }}
                                    {{ /if }}
                                  </div>
                                {{ /if }}
                                {{ @if (result.source) }}
                                  <div class="sc-nano-global-search-results result__source">Source: {{ result.source }}</div>
                                {{ /if }}
                              </div>
                            </a>
                          {{ /if }}
                          {{ @if (result.body) }}
                            <a class="sc-nano-global-search-results result__body" href="{{ result.url | abs_url(it, result) }}">{{ result._snippetResult.body.value | safe }}</a>
                          {{ /if }}
                          {{ @if (contentType === 'product' }}
                            <div class="sc-nano-global-search-results result__buttons">
                              <a class="sc-nano-global-search-results result__buttons-button" href="{{ result.url | abs_url(it, result) }}">View product</a>
                            </div>
                          {{ /if }}
                          {{ @if (contentType === 'event') }}
                            <a class="sc-nano-global-search-results result__promo" href="{{ result.url | abs_url(it, result) }}">
                              <div class="sc-nano-global-search-results result__promo-box">
                                <div class="sc-nano-global-search-results result__promo-bigdate">
                                  {{ result.start_date | date_long({year: undefined, month: 'short', day: undefined}) }}
                                  <span>{{ result.start_date | date_long({year: undefined, month: undefined, day: 'numeric'}) }}</span>
                                </div>
                                <div>
                                  <div class="sc-nano-global-search-results result__promo-date">
                                    <nano-icon class="sc-nano-global-search-results" name="light/calendar-alt"></nano-icon>
                                    {{ @if (result.end_date) }}
                                      {{ @if (result.end_date !== result.start_date) }}
                                        {{ result.start_date | date_long({weekday: 'short', day: 'numeric', month: 'short'}) }} - {{ result.end_date | date_long({weekday: 'short', day: 'numeric', year: 'numeric', month: 'short'}) }}
                                      {{ #else }}
                                        {{ result.start_date | date_long({weekday: 'short', day: 'numeric', year: 'numeric', month: 'short'}) }}
                                      {{ /if }}
                                    {{ #else }}
                                      {{ @if (result.start_date) }}
                                        {{ result.start_date | date_long({weekday: 'short', day: 'numeric', year: 'numeric', month: 'short'}) }}
                                      {{ /if }}
                                    {{ /if }}
                                  </div>
                                  <div class="sc-nano-global-search-results result__promo-venue">
                                    <nano-icon class="sc-nano-global-search-results" name="light/map-marker-alt"></nano-icon>
                                    Venue
                                  </div>
                                </div>
                              </div>
                            </a>
                          {{ /if }}
                          {{ @if (contentType !== 'lc_lightning_talk' && contentType !== 'video') }}
                            <div class="sc-nano-global-search-results result__meta">
                              {{ @if (result.type || result.content_type) }}
                                <div class="sc-nano-global-search-results result__type">
                                {{ @if (contentType === 'news_item') }}
                                  News
                                {{ #elif (contentType === 'static_page' || contentType === 'bespoke') }}
                                  Page
                                {{ #elif (contentType === 'case_study') }}
                                  Case study
                                {{ #elif (contentType === 'lc_poster') }}
                                  Poster
                                {{ #elif (contentType === 'nanopore_live') }}
                                  Live stream
                                {{ #elif (contentType === 'data_release') }}
                                  Data release
                                {{ #elif (contentType === 'research_area') }}
                                  Research area
                                {{ #elif (contentType === 'white_paper') }}
                                  White paper
                                {{ #elif (contentType === 'technical_document') }}
                                  Technical document
                                {{ #elif (contentType === 'info_sheet') }}
                                  Info sheet
                                {{ #elif (contentType === 'lc_home' || contentType === 'lc_venue') }}
                                  London Calling
                                {{ #else }}
                                  {{ contentType }}
                                {{ /if }}
                                </div>
                              {{ /if }}
                              {{ @if (contentType !== 'event') }}
                                {{ @if (result.updated) }}
                                  <div class="sc-nano-global-search-results result__date">{{ result.updated | date_long({year: 'numeric', month: 'short', day: 'numeric'}) }}</div>
                                {{ #else }}
                                  {{ @if (result.created) }}
                                    <div class="sc-nano-global-search-results result__date">{{ result.created | date_long({year: 'numeric', month: 'short', day: 'numeric'}) }}</div>
                                  {{ /if }}
                                {{ /if }}
                              {{ /if }}
                              {{ @if (result.tags) }}
                                <div class="sc-nano-global-search-results result__tags">
                                  {{ @if (typeof result.tags === 'string') }}
                                    {{ @if (result.tags !== "Resources" && result.tags !== "Tools" && result.tags !== "Publications" && result.tags !== "Case studies") }}
                                      <button data-value="{{ result.tags | lowercase }}" class="sc-nano-global-search-results result__tags-tag">{{ result.tags }}</button>
                                    {{ /if }}
                                  {{ #else }}
                                    {{ @each(result.tags) => tag, index }}
                                      {{ @if(index < 5)}}
                                        {{ @if (tag !== "Resources" && tag !== "Tools" && tag !== "Publications" && tag !== "Case studies") }}
                                          <button data-value="{{ tag | lowercase }}" class="sc-nano-global-search-results result__tags-tag">{{ tag }}</button>
                                        {{ /if }}
                                      {{ /if }}
                                    {{ /each }}
                                  {{ /if }}
                                </div>
                              {{ /if }}
                              {{ @if (result.source) }}
                                <div class="sc-nano-global-search-results result__source">Source: {{ result.source }}</div>
                              {{ /if }}
                            </div>
                          {{ /if }}
                        </div>
                      </li>
                    {{ /each }}
                  {{ /if }}
                </script>

                <div slot="start-output"></div>
                <ul slot="result-output" class="sc-nano-global-search-results"></ul>
              </nano-algolia-results>

              {{ @if(Object.keys(it.origFilters).length) }}
                <div class="sc-nano-global-search-results main-search__filters filters close-filters" id="main-search-filters">
                  <div class="sc-nano-global-search-results filters-wrap">
                    {{ @if(it.totalHits) }}
                      <div class="sc-nano-global-search-results filters-header">
                        <strong class="sc-nano-global-search-results filters-title">Filters</strong>
                        <button class="sc-nano-global-search-results filters-reset">Reset</button>
                        <button class="sc-nano-global-search-results filters__close-filters close-filters icon-button" aria-controls="main-search-filters">
                          <nano-icon class="sc-nano-global-search-results" name="light/times"></nano-icon>
                        </button>
                      </div>
                    {{ /if }}

                    <div>
                      <input class="sc-nano-global-search-results filter-cb old-posts filters__old-posts" type="checkbox" name="old-posts" id="old-posts" value="nice" />
                      <label class="sc-nano-global-search-results filter-label" for="old-posts">
                        <span>Include posts older than 2 years</span>
                      </label>
                      {{ @if(it.origFilters['type']) }}
                        <nano-details class="sc-nano-global-search-results" open={true} icon-rotation="180">
                          <strong slot="label">Type</strong>
                          <nano-icon slot="icon-end" name="light/chevron-down" size="small"></nano-icon>
                          <nano-algolia-filter store-id="searchf1" store-method="url-hash-push" filter-name="type">
                            <script type="text/template" slot="filter-template">
                              <ul class="sc-nano-global-search-results" data-num="{{(x = 0)}}">
                              {{ @foreach(it.dyn) => filter, filterVal }}
                                <li class="sc-nano-global-search-results" data-num="{{(x++)}}">
                                <input class="sc-nano-global-search-results filter-cb" type="checkbox" value="{{ filter }}" id="type-{{ filter }}-{{x}}" />
                                <label class="sc-nano-global-search-results filter-label" for="type-{{ filter }}-{{x}}">
                                  <span>{{ filter | public_name }} (<strong>{{ filterVal }}</strong>)</span>
                                </label></li>
                              {{ /foreach }}
                              </ul>
                            </script>
                            <div slot="output"></div>
                          </nano-algolia-filter>
                        </nano-details>
                      {{ /if }}

                      {{ @if(it.origFilters['channel']) }}
                        <nano-details open={true} icon-rotation="180">
                          <strong slot="label">Channels</strong>
                          <nano-icon slot="icon-end" name="light/chevron-down" size="small"></nano-icon>
                          <nano-algolia-filter store-id="searchf2" store-method="url-hash-push" filter-name="channel">
                            <script type="text/template" slot="filter-template">
                              <ul class="sc-nano-global-search-results" data-num="{{(x = 0)}}">
                              {{ @foreach(it.dyn) => filter, filterVal }}
                                <li class="sc-nano-global-search-results" data-num="{{(x++)}}">
                                <input class="filter-cb sc-nano-global-search-results" type="checkbox" value="{{ filter }}" id="channel-{{ filter }}-{{x}}" />
                                <label class="filter-label sc-nano-global-search-results" for="channel-{{ filter }}-{{x}}">
                                  <span>{{ filter | public_name }} (<strong>{{ filterVal }}</strong>)</span>
                                </label></li>
                              {{ /foreach }}
                              </ul>
                            </script>
                            <div slot="output"></div>
                          </nano-algolia-filter>
                        </nano-details>
                      {{ /if }}

                      {{ @if(it.origFilters['authors']) }}
                        <nano-details open={true} icon-rotation="180">
                          <strong slot="label">Authors</strong>
                          <nano-icon slot="icon-end" name="light/chevron-down" size="small"></nano-icon>
                          <nano-algolia-filter store-id="searchf3" store-method="url-hash-push" filter-name="authors">
                            <script type="text/template" slot="filter-template">
                              <ul class="sc-nano-global-search-results" data-num="{{(x = 0)}}">
                              {{ @foreach(it.dyn) => filter, filterVal }}
                                <li class="sc-nano-global-search-results" data-num="{{(x++)}}">
                                <input class="filter-cb sc-nano-global-search-results" type="checkbox" value="{{ filter }}" id="authors-{{ filter }}-{{x}}" />
                                <label class="filter-label sc-nano-global-search-results" for="authors-{{ filter }}-{{x}}">
                                  <span>{{ filter | public_name }} (<strong>{{ filterVal }}</strong>)</span>
                                </label></li>
                              {{ /foreach }}
                              </ul>
                            </script>
                            <div slot="output"></div>
                          </nano-algolia-filter>
                        </nano-details>
                      {{ /if }}

                      {{ @if(it.origFilters['categories_without_path']) }}
                        <nano-details open={true} icon-rotation="180">
                          <strong slot="label">Category</strong>
                          <nano-icon slot="icon-end" name="light/chevron-down" size="small"></nano-icon>
                          <nano-algolia-filter store-id="searchf4" store-method="url-hash-push" filter-name="categories_without_path">
                            <script type="text/template" slot="filter-template">
                              <ul class="sc-nano-global-search-results" data-num="{{(x = 0)}}">
                              {{ @foreach(it.dyn) => filter, filterVal }}
                                {{ @if(!filter.match(/publication/)) }}
                                  <li class="sc-nano-global-search-results" data-num="{{(x++)}}">
                                  <input class="sc-nano-global-search-results filter-cb" type="checkbox" value="{{ filter }}" id="cwp-{{ filter }}-{{x}}" />
                                  <label class="sc-nano-global-search-results filter-label" for="cwp-{{ filter }}-{{x}}">
                                    <span>{{ filter | public_name }} (<strong>{{ filterVal }}</strong>)</span>
                                  </label></li>
                                {{ /if }}
                              {{ /foreach }}
                              </ul>
                            </script>
                            <div slot="output"></div>
                          </nano-algolia-filter>
                        </nano-details>
                      {{ /if }}

                      {{ @if(it.origFilters['tags']) }}
                        <nano-details open={true} icon-rotation="180">
                          <strong slot="label">Tags</strong>
                          <nano-icon slot="icon-end" name="light/chevron-down" size="small"></nano-icon>
                          <nano-algolia-filter store-id="searchf5" store-method="url-hash-push" filter-name="tags">
                            <script type="text/template" slot="filter-template">
                              <ul class="sc-nano-global-search-results" data-num="{{(x = 0)}}">
                              {{ @foreach(it.dyn) => filter, filterVal }}
                                <li class="sc-nano-global-search-results" data-num="{{(x++)}}">
                                <input class="filter-cb sc-nano-global-search-results" type="checkbox" value="{{ filter | lowercase }}" id="tags-{{ filter }}-{{x}}" />
                                <label class="filter-label sc-nano-global-search-results" for="tags-{{ filter }}-{{x}}">
                                  <span>{{ filter | public_name }} (<strong>{{ filterVal }}</strong>)</span>
                                </label></li>
                              {{ /foreach }}
                              </ul>
                            </script>
                            <div slot="output"></div>
                          </nano-algolia-filter>
                        </nano-details>
                      {{ /if }}
                    </div>
                  </div>
                </div>
              {{ /if }}
            </div>`}
          </script>
          <div slot="output"></div>
          <slot />
        </nano-algolia>
      </Host>
    );
  }
}
